import { useState, useEffect, useMemo, useRef } from 'react';
import { useOnboardingStore } from '../../../store/onboardingStore';
import { useAuth } from '../../../context/AuthContext';
import { dictionary, Language } from '../../../utils/dictionary';
import { FunnelConfig, SalesAngle, LegacyTemplate, PromotionConfig, PromotionTimeLimit, PromotionType } from '../../../types/onboarding';
import { Check, ArrowLeft, ArrowRight, Pencil, ChevronDown, ChevronUp, Sparkles, Target, BarChart3, Maximize2, X, Gift, Percent, Clock, AlertCircle, RefreshCw } from 'lucide-react';
import Loader from '../../common/Loader';
import type { DesignStructureRecommendedSection } from '../../../services/designStructure/designStructureService';
import type { AdStyle } from '../../../feature/ads/cretives/components/modals/steps/adStudio/types';
import { LazyImage } from '../../ui/lazy-image';
import { buildOnboardingOptionsFromBindings, type OnboardingTemplateOption } from '../../../templateStudio/onboardingTemplateOptions';
import { buildPromotionLabel, createEmptyPromotion, getPromotionTypeLabel, isConfiguredPromotionType, normalizePromotion, normalizePromotionTimeLimit } from '../../../utils/promotions';

type LoadingScreenStep = {
  icon: typeof Sparkles;
  label: string;
};

// ─── Main Component ─────────────────────────────────────────
export function StepFunnelConfig({
  onComplete,
  onBack,
  onSubStepChange,
  isFetchingSalesAngles = false,
  salesAnglesError = null,
  onRetrySalesAngles,
}: {
  onComplete?: () => void;
  onBack?: () => void;
  onSubStepChange?: (subStep: number) => void;
  isFetchingSalesAngles?: boolean;
  salesAnglesError?: string | null;
  onRetrySalesAngles?: () => void;
} = {}) {
  const { language } = useAuth();
  const {
    data, multiFunnelData, updateFunnelConfig, salesAngles,
    isFetchingTemplates, fetchSectionTemplates,
    onboardingBindings, isFetchingBindings, fetchOnboardingBindings,
    setFunnelDesign,
    fetchDesignRecommendation,
    isFetchingSalesAngleRecommendation, hasFetchedSalesAngles,
    adTemplates, isFetchingAdTemplates, isRankingPageTemplates, hasRankedPageTemplates, fetchAdTemplates, rankOnboardingTemplates, toggleFunnelAdTemplate,
    initializeFunnels,
    selectedLegacyTemplates, clearLegacyTemplates,
  } = useOnboardingStore();

  const [activeSubStep, setActiveSubStepRaw] = useState<1 | 2 | 3>(1);
  const [isWaitingForFunnelDesigns, setIsWaitingForFunnelDesigns] = useState(false);
  const [designSelectionError, setDesignSelectionError] = useState<string | null>(null);
  const [showPromotions, setShowPromotions] = useState(false);
  const [preparedSelectionSignature, setPreparedSelectionSignature] = useState<string | null>(null);
  const designPreparationPromiseRef = useRef<Promise<void> | null>(null);
  const designPreparationSignatureRef = useRef<string | null>(null);
  const latestSelectionSignatureRef = useRef<string | null>(null);
  const setActiveSubStep = (step: 1 | 2 | 3) => {
    setActiveSubStepRaw(step);
    onSubStepChange?.(step);
  };
  // New (AI) template IDs in selection order
  const [selectedTemplateOrder, setSelectedTemplateOrder] = useState<string[]>([]);
  const selectionSignature = useMemo(
    () => JSON.stringify({
      newTemplates: selectedTemplateOrder,
      legacyTemplates: selectedLegacyTemplates.map(template => template.id),
    }),
    [selectedTemplateOrder, selectedLegacyTemplates]
  );
  const onboardingTemplateOptions = useMemo(
    () => buildOnboardingOptionsFromBindings(onboardingBindings),
    [onboardingBindings]
  );
  const isFetchingTemplateCards = isFetchingTemplates || isFetchingBindings;

  useEffect(() => {
    latestSelectionSignatureRef.current = selectionSignature;
    setDesignSelectionError(null);
    setPreparedSelectionSignature(current =>
      current === selectionSignature ? current : null
    );
  }, [selectionSignature]);

  useEffect(() => {
    onSubStepChange?.(1);
  }, [onSubStepChange]);

  useEffect(() => {
    clearLegacyTemplates();
  }, [clearLegacyTemplates]);

  useEffect(() => {
    setSelectedTemplateOrder(prev =>
      prev.filter(templateId => onboardingTemplateOptions.some(option => option.id === templateId))
    );
  }, [onboardingTemplateOptions]);

  const funnels = multiFunnelData.funnels;
  const newCount = selectedTemplateOrder.length;
  const legacyCount = selectedLegacyTemplates.length;
  const totalSelected = newCount + legacyCount;
  const hasActivePromotions = funnels.some(funnel => funnel.promotion?.enabled);

  useEffect(() => {
    fetchSectionTemplates();
    fetchOnboardingBindings();
    fetchAdTemplates();
  }, [fetchSectionTemplates, fetchOnboardingBindings, fetchAdTemplates]);

  useEffect(() => {
    const canRankPages = !isFetchingBindings && onboardingBindings.length > 0;
    const canRankAds = !isFetchingAdTemplates && adTemplates.length > 0;
    if (canRankPages || canRankAds) {
      rankOnboardingTemplates();
    }
  }, [isFetchingBindings, isFetchingAdTemplates, onboardingBindings.length, adTemplates.length, rankOnboardingTemplates]);

  // Auto-assign angles when entering sub-step 2
  useEffect(() => {
    if (activeSubStep !== 2 || salesAngles.length === 0) return;
    funnels.forEach((funnel, index) => {
      if (funnel.selectedAngles.length === 0) {
        const angle = salesAngles[index % salesAngles.length];
        updateFunnelConfig(index, {
          selectedAngles: [{ name: angle.title, description: angle.description }],
        });
      }
    });
  }, [activeSubStep, salesAngles.length, funnels.length, updateFunnelConfig]);

  useEffect(() => {
    if (activeSubStep !== 2) return;
    setShowPromotions(hasActivePromotions);
  }, [activeSubStep, hasActivePromotions]);

  // Template selection handler — no limit, free mixing with legacy
  const handleToggleTemplate = (templateId: string) => {
    setSelectedTemplateOrder(prev => {
      const alreadySelected = prev.includes(templateId);
      if (alreadySelected) {
        return prev.filter(id => id !== templateId);
      }
      return [...prev, templateId];
    });
  };

  // Change angle for a funnel
  const handleChangeAngle = (funnelIndex: number, angleId: string) => {
    const angle = salesAngles.find(a => a.id === angleId);
    if (angle) {
      updateFunnelConfig(funnelIndex, {
        selectedAngles: [{ name: angle.title, description: angle.description }],
      });
    }
  };

  // Set custom angle for a funnel
  const handleSetCustomAngle = (funnelIndex: number, name: string, description: string) => {
    updateFunnelConfig(funnelIndex, {
      selectedAngles: [{ name, description }],
    });
  };

  const handleOpenPromotions = () => {
    setShowPromotions(true);
    funnels.forEach((funnel, index) => {
      if (!funnel.promotion?.enabled) {
        updateFunnelConfig(index, { promotion: createEmptyPromotion() });
      }
    });
  };

  const handleChangePromotion = (funnelIndex: number, promotion: PromotionConfig | null) => {
    updateFunnelConfig(funnelIndex, { promotion });
    const nextHasActivePromotions = funnels.some((funnel, index) =>
      index === funnelIndex ? Boolean(promotion?.enabled) : Boolean(funnel.promotion?.enabled)
    );
    if (!nextHasActivePromotions) {
      setShowPromotions(false);
    }
  };

  const getSelectedNewTemplates = () =>
    selectedTemplateOrder
      .map((templateId) => onboardingTemplateOptions.find(option => option.id === templateId))
      .filter((template): template is OnboardingTemplateOption => Boolean(template));

  const getTemplateDesign = (template: OnboardingTemplateOption) => (
    template.design
      ? { ...template.design, previewUrl: template.previewUrl }
      : { previewUrl: template.previewUrl }
  );

  const havePreparedTemplateFunnels = (expectedNewCount = newCount) => {
    if (expectedNewCount === 0) return true;
    const preparedFunnels = useOnboardingStore.getState().multiFunnelData.funnels.slice(0, expectedNewCount);
    return preparedFunnels.length === expectedNewCount &&
      preparedFunnels.every(funnel =>
        Boolean(funnel.designTemplateType) &&
        funnel.sections.some(section => !section.isStandard && section.enabled)
      );
  };

  const startFunnelDesignPreparation = () => {
    const signatureForRun = selectionSignature;

    if (newCount === 0) {
      setPreparedSelectionSignature(signatureForRun);
      return Promise.resolve();
    }

    if (
      designPreparationPromiseRef.current &&
      designPreparationSignatureRef.current === signatureForRun
    ) {
      return designPreparationPromiseRef.current;
    }

    const selectedTemplates = getSelectedNewTemplates();
    const selectedTemplateById = new Map(selectedTemplates.map(template => [template.id, template]));

    const preparationPromise = (async () => {
      setDesignSelectionError(null);

      const recommendedSectionsByTemplateType = new Map<string, DesignStructureRecommendedSection[] | undefined>();

      for (const templateType of Array.from(new Set(selectedTemplates.map(template => template.templateType)))) {
        if (latestSelectionSignatureRef.current !== signatureForRun) return;

        try {
          const recommendation = await fetchDesignRecommendation([templateType], true);
          recommendedSectionsByTemplateType.set(
            templateType,
            recommendation?.recommended_sections?.length ? recommendation.recommended_sections : undefined
          );
        } catch (error) {
          console.warn(`design_structure_selector failed for template ${templateType}; enabling all sections.`, error);
          recommendedSectionsByTemplateType.set(templateType, undefined);
        }
      }

      if (latestSelectionSignatureRef.current !== signatureForRun) return;

      for (const [index, templateId] of selectedTemplateOrder.entries()) {
        if (latestSelectionSignatureRef.current !== signatureForRun) return;

        const template = selectedTemplateById.get(templateId);
        if (!template) continue;

        const recommendedSections = recommendedSectionsByTemplateType.get(template.templateType);
        await setFunnelDesign(index, template.id, template.templateType, recommendedSections, getTemplateDesign(template));
      }

      if (latestSelectionSignatureRef.current !== signatureForRun) return;

      if (!havePreparedTemplateFunnels(selectedTemplateOrder.length)) {
        throw new Error('Template Studio sections were not prepared for all selected funnels.');
      }

      setPreparedSelectionSignature(signatureForRun);
    })().finally(() => {
      if (designPreparationPromiseRef.current === preparationPromise) {
        designPreparationPromiseRef.current = null;
        designPreparationSignatureRef.current = null;
      }
    });

    designPreparationPromiseRef.current = preparationPromise;
    designPreparationSignatureRef.current = signatureForRun;
    return preparationPromise;
  };

  const ensureFunnelDesignsPrepared = async () => {
    if (newCount === 0) return true;

    if (preparedSelectionSignature === selectionSignature && havePreparedTemplateFunnels(newCount)) {
      return true;
    }

    setIsWaitingForFunnelDesigns(true);

    try {
      const signatureForRun = selectionSignature;
      await startFunnelDesignPreparation();

      if (latestSelectionSignatureRef.current !== signatureForRun) {
        return false;
      }

      if (!havePreparedTemplateFunnels(newCount)) {
        throw new Error('Template Studio sections are still missing after preparation.');
      }

      setPreparedSelectionSignature(signatureForRun);
      return true;
    } catch (error) {
      console.error('Error preparing funnel designs:', error);
      setDesignSelectionError(
        dictionary["design-selection-error"]?.[language] ??
        "No pudimos seleccionar la estructura del funnel. Intenta de nuevo."
      );
      return false;
    } finally {
      setIsWaitingForFunnelDesigns(false);
    }
  };

  const completeAfterDesignPreparation = async () => {
    if (isWaitingForFunnelDesigns) return;
    const isPrepared = await ensureFunnelDesignsPrepared();
    if (isPrepared) onComplete?.();
  };

  // Navigation
  const handleNext = async () => {
    if (activeSubStep === 1) {
      if (totalSelected === 0) return;

      if (preparedSelectionSignature === selectionSignature && funnels.length === totalSelected) {
        setActiveSubStep(2);
        return;
      }

      setDesignSelectionError(null);

      // Initialize local onboarding drafts immediately. The Template Studio
      // section structure is prepared in the background and verified before
      // the summary/final submit path can continue.
      initializeFunnels(totalSelected);
      if (newCount === 0) {
        setPreparedSelectionSignature(selectionSignature);
      }
      setActiveSubStep(2);
      void startFunnelDesignPreparation().catch((error) => {
        console.error('Error preparing funnel designs:', error);
        setDesignSelectionError(
          dictionary["design-selection-error"]?.[language] ??
          "No pudimos seleccionar la estructura del funnel. Intenta de nuevo."
        );
      });
    } else if (activeSubStep === 2) {
      // Always show ads step — both legacy and new templates can have ads
      setActiveSubStep(3);
    } else if (activeSubStep === 3) {
      await completeAfterDesignPreparation();
    }
  };

  const handleBack = () => {
    if (activeSubStep > 1) {
      setActiveSubStep((activeSubStep - 1) as 1 | 2 | 3);
    } else {
      onBack?.();
    }
  };

  const getNextLabel = () => {
    if (activeSubStep === 3) {
      return dictionary["continue"]?.[language] ?? "Continuar";
    }
    return dictionary["next"]?.[language] ?? dictionary["continue"]?.[language] ?? "Siguiente";
  };

  const isNextDisabled =
    (activeSubStep === 1 && totalSelected === 0) ||
    (activeSubStep === 2 && (isFetchingSalesAngles || isFetchingSalesAngleRecommendation || salesAngles.length === 0)) ||
    (activeSubStep === 3 && isWaitingForFunnelDesigns);

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* No stepper header — hidden per user request */}

      {/* Sub-step content */}
      <div className="flex-1 min-h-0">
        <div className={activeSubStep !== 1 ? 'hidden' : 'h-full'}>
          <DesignTabGlobal
            templateOptions={onboardingTemplateOptions}
            isFetchingTemplates={isFetchingTemplateCards}
            isRankingTemplates={isRankingPageTemplates}
            hasRankedTemplates={hasRankedPageTemplates}
            selectedTemplateOrder={selectedTemplateOrder}
            onToggleTemplate={handleToggleTemplate}
            totalSelected={totalSelected}
            language={language}
          />
        </div>

        <div className={activeSubStep !== 2 ? 'hidden' : 'h-full min-h-0 flex flex-col'}>
          <AngleAssignmentTab
            funnels={funnels}
            templateOptions={onboardingTemplateOptions}
            selectedTemplateOrder={selectedTemplateOrder}
            selectedLegacyTemplates={selectedLegacyTemplates}
            newCount={newCount}
            salesAngles={salesAngles}
            hasFetchedSalesAngles={hasFetchedSalesAngles}
            isFetchingSalesAngles={isFetchingSalesAngles}
            isFetchingSalesAngleRecommendation={isFetchingSalesAngleRecommendation}
            salesAnglesError={salesAnglesError}
            onRetrySalesAngles={onRetrySalesAngles}
            showPromotions={showPromotions}
            onOpenPromotions={handleOpenPromotions}
            onChangeAngle={handleChangeAngle}
            onSetCustomAngle={handleSetCustomAngle}
            onChangePromotion={handleChangePromotion}
            productPrice={data.productData.price}
            language={language}
          />
        </div>

        <div className={activeSubStep !== 3 ? 'hidden' : 'h-full'}>
          <AdsGlobalTab
            funnels={funnels}
            adTemplates={adTemplates}
            isFetchingAdTemplates={isFetchingAdTemplates}
            onToggleAdTemplate={(templateId) => {
              // Toggle ad for ALL funnels at once
              funnels.forEach((_, index) => toggleFunnelAdTemplate(index, templateId));
            }}
            language={language}
          />
        </div>
      </div>

      {/* Navigation buttons — fixed at bottom, never scrolls */}
      <div className="shrink-0 flex justify-between items-center pt-3">
        <button
          onClick={handleBack}
          className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          {dictionary["back"]?.[language] ?? "Atrás"}
        </button>
        <div className="flex items-center gap-3">
          {designSelectionError && (activeSubStep === 1 || activeSubStep === 3) && (
            <span className="max-w-xs text-right text-xs font-medium text-red-500">
              {designSelectionError}
            </span>
          )}
          {activeSubStep === 3 && (
            <button
              onClick={completeAfterDesignPreparation}
              disabled={isWaitingForFunnelDesigns}
              className="text-sm text-gray-400 hover:text-gray-600 transition-colors"
            >
              {dictionary["skip"]?.[language] ?? "Omitir"}
            </button>
          )}
          <button
            onClick={handleNext}
            disabled={isNextDisabled}
            className={`flex items-center gap-2 px-7 py-2.5 text-sm text-white rounded-full font-medium transition-all ${
              isNextDisabled
                ? 'bg-[#E2E8F0] cursor-not-allowed'
                : 'bg-fluxi hover:bg-fluxi-dark'
            }`}
          >
            {isWaitingForFunnelDesigns ? (
              <Loader size={16} />
            ) : (
              <>
                {getNextLabel()}
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function AnalysisLoadingScreen({
  title,
  subtitle,
  steps,
}: {
  title: string;
  subtitle: string;
  steps: LoadingScreenStep[];
}) {
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % steps.length);
    }, 2500);
    return () => clearInterval(interval);
  }, [steps.length]);

  return (
    <div className="flex min-h-[520px] flex-col items-center justify-center px-4 py-8">
      <div className="relative mb-8">
        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-fluxi to-fluxi flex items-center justify-center animate-pulse">
          <Sparkles className="w-10 h-10 text-white" />
        </div>
        <div className="absolute inset-0 w-20 h-20 rounded-full bg-gradient-to-br from-fluxi to-fluxi opacity-20 animate-ping" />
      </div>

      <h2 className="text-xl md:text-2xl font-bold text-gray-900 text-center mb-2">
        {title}
      </h2>
      <p className="text-sm text-[#64748B] text-center mb-10 max-w-md">
        {subtitle}
      </p>

      <div className="w-full max-w-sm space-y-3">
        {steps.map((step, index) => {
          const Icon = step.icon;
          const isActive = index === activeStep;
          const isDone = index < activeStep;

          return (
            <div
              key={index}
              className={`flex items-center gap-3 px-4 py-3 rounded-2xl transition-all duration-500 ${
                isActive
                  ? 'bg-[#F5F8FF] border border-fluxi/20 shadow-sm'
                  : isDone
                    ? 'bg-[#F0FDF4] border border-green-200'
                    : 'bg-[#F8FAFC] border border-transparent'
              }`}
            >
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-500 ${
                  isActive
                    ? 'bg-fluxi text-white'
                    : isDone
                      ? 'bg-green-500 text-white'
                      : 'bg-[#E2E8F0] text-gray-400'
                }`}
              >
                {isDone ? (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <Icon className="w-4 h-4" />
                )}
              </div>
              <span
                className={`text-sm font-medium transition-all duration-500 ${
                  isActive ? 'text-fluxi' : isDone ? 'text-green-600' : 'text-gray-400'
                }`}
              >
                {step.label}
              </span>
              {isActive && (
                <div className="ml-auto">
                  <Loader size={16} color="#4368E5" />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Sub-step 1: Global Template Selection ──────────────────
function DesignTabGlobal({
  templateOptions,
  isFetchingTemplates,
  isRankingTemplates,
  hasRankedTemplates,
  selectedTemplateOrder,
  onToggleTemplate,
  totalSelected,
  language,
}: {
  templateOptions: OnboardingTemplateOption[];
  isFetchingTemplates: boolean;
  isRankingTemplates: boolean;
  hasRankedTemplates: boolean;
  selectedTemplateOrder: string[];
  onToggleTemplate: (templateId: string) => void;
  totalSelected: number;
  language: Language;
}) {
  const styleTemplateOptions = useMemo(
    () => templateOptions.filter(template => template.isStyleVariant),
    [templateOptions]
  );
  const customImageTemplateOptions = useMemo(
    () => templateOptions.filter(template => !template.isStyleVariant),
    [templateOptions]
  );
  const hasTemplates = templateOptions.length > 0;
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [expandedTemplate, setExpandedTemplate] = useState<OnboardingTemplateOption | null>(null);

  const getNewSelectionNumber = (templateId: string) => {
    const idx = selectedTemplateOrder.indexOf(templateId);
    return idx >= 0 ? idx + 1 : 0;
  };

  useEffect(() => {
    if (!expandedTemplate) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setExpandedTemplate(null);
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [expandedTemplate]);

  return (
    <div>
      <div className="mb-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-900">
            {dictionary["select-template-per-funnel"]?.[language] ?? "Select one template for each funnel you want to create"}
          </h2>
          {totalSelected > 0 && (
            <span className="text-sm font-bold text-fluxi whitespace-nowrap ml-3">
              {totalSelected} {totalSelected === 1 ? 'funnel' : 'funnels'}
            </span>
          )}
        </div>
      </div>

      {isFetchingTemplates || isRankingTemplates || !hasRankedTemplates ? (
        <div className="flex min-h-[calc(100vh-360px)] flex-col items-center justify-center text-center">
          <Loader size={36} />
          <p className="mt-4 text-sm font-medium text-gray-700">
            {dictionary["preparing-recommended-templates"]?.[language] ?? "Preparando templates recomendados"}
          </p>
        </div>
      ) : (
        <div className="max-h-[calc(100vh-280px)] overflow-y-auto pr-1 py-1 space-y-6">
          {styleTemplateOptions.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                {dictionary["classic-templates-label"]?.[language] ?? "Plantillas clásicas"}
              </p>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
                {styleTemplateOptions.map((template) => (
                  <TemplatePreviewCard
                    key={template.id}
                    template={template}
                    isSelected={selectedTemplateOrder.includes(template.id)}
                    isHovered={hoveredId === template.id && !selectedTemplateOrder.includes(template.id)}
                    selectionNumber={getNewSelectionNumber(template.id)}
                    onToggle={() => onToggleTemplate(template.id)}
                    onExpand={() => setExpandedTemplate(template)}
                    onHoverStart={() => setHoveredId(template.id)}
                    onHoverEnd={() => setHoveredId(null)}
                  />
                ))}
              </div>
            </div>
          )}
          {customImageTemplateOptions.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                {dictionary["ai-templates-label"]?.[language] ?? "Plantillas nuevas"}
              </p>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
                {customImageTemplateOptions.map((template) => (
                  <TemplatePreviewCard
                    key={template.id}
                    template={template}
                    isSelected={selectedTemplateOrder.includes(template.id)}
                    isHovered={hoveredId === template.id && !selectedTemplateOrder.includes(template.id)}
                    selectionNumber={getNewSelectionNumber(template.id)}
                    onToggle={() => onToggleTemplate(template.id)}
                    onExpand={() => setExpandedTemplate(template)}
                    onHoverStart={() => setHoveredId(template.id)}
                    onHoverEnd={() => setHoveredId(null)}
                    dimWhenSelected
                  />
                ))}
              </div>
            </div>
          )}
          {!hasTemplates && (
            <div className="flex min-h-[280px] items-center justify-center rounded-2xl border border-dashed border-gray-200 bg-gray-50 text-center">
              <p className="max-w-sm text-sm font-medium text-gray-500">
                No encontramos el template sophisticated HTML activo para onboarding.
              </p>
            </div>
          )}
        </div>
      )}

      {expandedTemplate && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-white/88 px-4 py-6 backdrop-blur-sm sm:px-6"
          onClick={() => setExpandedTemplate(null)}
        >
          <button
            type="button"
            aria-label="Cerrar preview"
            onClick={() => setExpandedTemplate(null)}
            className="fixed right-5 top-5 z-[101] flex h-11 w-11 items-center justify-center rounded-full bg-white/95 text-gray-700 shadow-[0_12px_28px_rgba(15,23,42,0.18)] ring-1 ring-black/5 transition-colors hover:bg-white hover:text-gray-950 focus:outline-none focus:ring-2 focus:ring-fluxi/40"
          >
            <X className="h-5 w-5" />
          </button>

          <div
            className="mx-auto max-h-[78vh] w-full max-w-[390px] overflow-y-auto overscroll-contain rounded-[28px] bg-white shadow-[0_30px_90px_rgba(15,23,42,0.18)] ring-1 ring-black/5 sm:max-w-[420px]"
            onClick={(event) => event.stopPropagation()}
          >
              {expandedTemplate.previewUrl ? (
                <img
                  src={expandedTemplate.previewUrl}
                  alt={expandedTemplate.name}
                  className="h-auto w-full bg-white"
                  loading="eager"
                />
              ) : (
                <div className="aspect-[9/16] w-full bg-gradient-to-b from-fluxi to-fluxi" />
              )}
          </div>
        </div>
      )}
    </div>
  );
}

function TemplatePreviewCard({
  template,
  isSelected,
  isHovered,
  selectionNumber,
  onToggle,
  onExpand,
  onHoverStart,
  onHoverEnd,
  dimWhenSelected = false,
}: {
  template: OnboardingTemplateOption;
  isSelected: boolean;
  isHovered: boolean;
  selectionNumber: number;
  onToggle: () => void;
  onExpand: () => void;
  onHoverStart: () => void;
  onHoverEnd: () => void;
  dimWhenSelected?: boolean;
}) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onToggle}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onToggle();
        }
      }}
      onMouseEnter={onHoverStart}
      onMouseLeave={onHoverEnd}
      className={`group relative w-full cursor-pointer rounded-lg overflow-hidden transition-all duration-300 ${
        isSelected
          ? `${dimWhenSelected ? 'opacity-50 ' : ''}ring-2 ring-fluxi`
          : 'hover:ring-1 hover:ring-gray-300'
      } border border-gray-300`}
    >
      {template.thumbnailUrl ? (
        <div className="relative w-full aspect-[9/16] bg-gray-100 overflow-hidden">
          <img
            src={template.thumbnailUrl}
            alt={template.name}
            className="w-full h-full object-cover"
            style={{
              objectPosition: isSelected
                ? 'center top'
                : isHovered
                  ? 'center bottom'
                  : 'center top',
              transition: isSelected
                ? 'none'
                : isHovered
                  ? 'object-position 30s linear'
                  : 'object-position 0.5s ease-out',
            }}
            loading="lazy"
          />
          <button
            type="button"
            aria-label={`Expandir ${template.name}`}
            onClick={(event) => {
              event.stopPropagation();
              onExpand();
            }}
            className="absolute bottom-2 right-2 flex h-8 w-8 items-center justify-center rounded-full bg-black/65 text-white opacity-0 shadow-lg backdrop-blur-sm transition-opacity duration-200 hover:bg-black/80 focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-white/80 group-hover:opacity-100"
          >
            <Maximize2 className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <div className="w-full aspect-[9/16] bg-gradient-to-b from-fluxi to-fluxi" />
      )}
      {isSelected && (
        <div className="absolute top-2 right-2 w-6 h-6 bg-fluxi rounded-full flex items-center justify-center shadow">
          <span className="text-xs font-bold text-white">{selectionNumber}</span>
        </div>
      )}
    </div>
  );
}

// ─── Sub-step 2: Angle Assignment (template ↔ angle) ────────
function AngleAssignmentTab({
  funnels,
  templateOptions,
  selectedTemplateOrder,
  selectedLegacyTemplates,
  newCount,
  salesAngles,
  hasFetchedSalesAngles,
  isFetchingSalesAngles,
  isFetchingSalesAngleRecommendation,
  salesAnglesError,
  onRetrySalesAngles,
  showPromotions,
  onOpenPromotions,
  onChangeAngle,
  onSetCustomAngle,
  onChangePromotion,
  productPrice,
  language,
}: {
  funnels: FunnelConfig[];
  templateOptions: OnboardingTemplateOption[];
  selectedTemplateOrder: string[];
  selectedLegacyTemplates: LegacyTemplate[];
  newCount: number;
  salesAngles: SalesAngle[];
  hasFetchedSalesAngles: boolean;
  isFetchingSalesAngles: boolean;
  isFetchingSalesAngleRecommendation: boolean;
  salesAnglesError: string | null;
  onRetrySalesAngles?: () => void;
  showPromotions: boolean;
  onOpenPromotions: () => void;
  onChangeAngle: (funnelIndex: number, angleId: string) => void;
  onSetCustomAngle: (funnelIndex: number, name: string, description: string) => void;
  onChangePromotion: (funnelIndex: number, promotion: PromotionConfig | null) => void;
  productPrice: string;
  language: Language;
}) {
  const [expandedFunnel, setExpandedFunnel] = useState<number | null>(null);
  const [editingFunnelIndex, setEditingFunnelIndex] = useState<number | null>(null);
  const [editedName, setEditedName] = useState('');
  const [editedDescription, setEditedDescription] = useState('');
  const isCompactFunnelList = funnels.length <= 2;

  if (isFetchingSalesAngles || isFetchingSalesAngleRecommendation || (!hasFetchedSalesAngles && salesAngles.length === 0)) {
    return <AnglesLoadingScreen language={language} />;
  }

  if (salesAngles.length === 0) {
    return (
      <AnglesRetryScreen
        language={language}
        message={salesAnglesError}
        onRetry={onRetrySalesAngles}
      />
    );
  }

  return (
    <div className={`${showPromotions ? 'max-w-[59.5rem]' : 'max-w-2xl'} mx-auto h-full flex flex-col min-h-0 transition-[max-width] duration-300`}>
      <div className={`${isCompactFunnelList ? 'max-h-[calc(100vh-260px)]' : 'min-h-0 flex-1'} overflow-y-auto pr-1`}>
        <div className="mb-4 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-lg font-bold text-gray-900">
              Configura los ángulos de venta y promociones
            </h2>
          </div>
          <button
            type="button"
            onClick={onOpenPromotions}
            disabled={showPromotions}
            className={`inline-flex shrink-0 items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${
              showPromotions ? 'cursor-default border-gray-200 bg-gray-100 text-gray-400' : 'border-[#DDE2F1] bg-white text-gray-700 hover:border-fluxi/40 hover:text-fluxi'
            }`}
          >
            <Gift className="h-4 w-4" />
            {showPromotions ? 'Promociones agregadas' : 'Agregar promociones'}
          </button>
        </div>
        <div className="space-y-3 w-full">
        {funnels.map((funnel, funnelIndex) => {
          // First newCount funnels are new templates, rest are legacy
          const isLegacyFunnel = funnelIndex >= newCount;
          const legacyIndex = funnelIndex - newCount;
          const legacyTemplate = isLegacyFunnel ? selectedLegacyTemplates[legacyIndex] : null;
          const templateId = !isLegacyFunnel ? selectedTemplateOrder[funnelIndex] : null;
          const template = templateId ? templateOptions.find(option => option.id === templateId) : null;
          const legacyPreviewUrl = legacyTemplate?.previewUrl;
          const currentAngle = funnel.selectedAngles[0];
          const currentAngleObj = currentAngle
            ? salesAngles.find(a => a.id === currentAngle.name || a.title === currentAngle.name)
            : null;
          const isEditing = editingFunnelIndex === funnelIndex;
          const isExpanded = expandedFunnel === funnelIndex;

          return (
            <div
              key={funnel.id}
              className={`grid items-start gap-5 p-4 rounded-2xl border border-[#E2E8F0] bg-[#FAFAFF] ${
                showPromotions ? 'grid-cols-1 md:grid-cols-[8rem_minmax(0,31rem)_minmax(15rem,16rem)]' : 'grid-cols-[8rem_minmax(0,1fr)]'
              }`}
            >
              {/* Template preview — wider column, 9:16 aspect */}
              <div className="w-32">
                {legacyPreviewUrl ? (
                  <div className="w-full aspect-[9/16] rounded-xl overflow-hidden border border-[#E2E8F0] shadow-sm">
                    <img src={legacyPreviewUrl} alt={legacyTemplate?.name} className="w-full h-full object-cover object-top" />
                  </div>
                ) : template?.previewUrl ? (
                  <div className="w-full aspect-[9/16] rounded-xl overflow-hidden border border-[#E2E8F0] shadow-sm">
                    <img src={template.thumbnailUrl || template.previewUrl} alt={template.name} className="w-full h-full object-cover object-top" />
                  </div>
                ) : (
                  <div className="w-full aspect-[9/16] rounded-xl bg-gradient-to-b from-fluxi to-fluxi" />
                )}
              </div>

              {/* Angle info */}
              <div className="flex-1 min-w-0">
                {/* Funnel label */}
                <p className="py-3 text-sm font-bold text-gray-400">
                  {funnels.length > 1 ? `Ángulo del embudo ${funnelIndex + 1}:` : 'Ángulo del embudo:'}
                </p>

                {/* Current angle — display OR edit */}
                {isEditing ? (
                  <div className="space-y-3 mb-3">
                    <input
                      type="text"
                      value={editedName}
                      onChange={(e) => setEditedName(e.target.value)}
                      placeholder={dictionary["custom-angle-name-placeholder"]?.[language] ?? "Nombre del ángulo"}
                      className="w-full text-sm text-gray-700 border border-gray-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-1 focus:ring-fluxi/50"
                      autoFocus
                    />
                    <textarea
                      value={editedDescription}
                      onChange={(e) => setEditedDescription(e.target.value)}
                      placeholder={dictionary["custom-angle-desc-placeholder"]?.[language] ?? "Describe tu enfoque de venta..."}
                      className="w-full text-sm text-gray-700 border border-gray-200 rounded-lg p-3 resize-none focus:outline-none focus:ring-1 focus:ring-fluxi/50"
                      rows={3}
                    />
                    <div className="flex gap-2.5">
                      <button
                        onClick={() => {
                          onSetCustomAngle(funnelIndex, editedName.trim() || currentAngle?.name || '', editedDescription.trim());
                          setEditingFunnelIndex(null);
                        }}
                        className="px-5 py-2 text-sm font-medium text-white bg-fluxi rounded-full hover:bg-[#3535e0] transition-colors"
                      >
                        {dictionary["save-changes"]?.[language] ?? "Guardar cambios"}
                      </button>
                      <button
                        onClick={() => setEditingFunnelIndex(null)}
                        className="px-5 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-full hover:bg-gray-50 transition-colors"
                      >
                        {dictionary.cancel?.[language] ?? "Cancelar"}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="mb-3">
                    <p className="text-base font-bold text-gray-900">
                      {currentAngleObj?.title || currentAngle?.name || '—'}
                    </p>
                    {currentAngle?.description && (
                      <p className="text-sm text-gray-500 leading-relaxed mt-1">
                        {currentAngle.description}
                      </p>
                    )}
                  </div>
                )}

                {/* Action buttons row */}
                {!isEditing && (
                  <div className="flex flex-wrap gap-2.5 justify-end mt-5">
                    <button
                      onClick={() => {
                        setEditingFunnelIndex(funnelIndex);
                        setEditedName(currentAngleObj?.title || currentAngle?.name || '');
                        setEditedDescription(currentAngle?.description || '');
                        setExpandedFunnel(null);
                      }}
                      className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-full hover:bg-gray-50 transition-colors shadow-[0_1px_2px_rgba(0,0,0,0.04)]"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                      {dictionary["edit-angle"]?.[language] ?? "Editar"}
                    </button>
                    <button
                      onClick={() => {
                        setExpandedFunnel(isExpanded ? null : funnelIndex);
                        setEditingFunnelIndex(null);
                      }}
                      className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-full hover:bg-gray-50 transition-colors shadow-[0_1px_2px_rgba(0,0,0,0.04)]"
                    >
                      {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                      {dictionary["other-angles"]?.[language] ?? "Otros ángulos"}
                    </button>
                  </div>
                )}

                {/* Expanded: other angle options */}
                {isExpanded && (
                  <div className="mt-3 space-y-2">
                    {salesAngles.map((angle) => {
                      const isActive = currentAngle?.name === angle.id || currentAngle?.name === angle.title;
                      return (
                        <button
                          key={angle.id}
                          onClick={() => {
                            onChangeAngle(funnelIndex, angle.id);
                            setExpandedFunnel(null);
                          }}
                          className={`w-full text-left px-4 py-3 rounded-xl transition-all ${
                            isActive
                              ? 'bg-white border-[1.5px] border-fluxi/40 text-gray-900'
                              : 'bg-white border border-[#E2E8F0] text-gray-600 hover:border-gray-300'
                          }`}
                        >
                          <p className="text-sm font-semibold text-gray-900">{angle.title}</p>
                          {angle.description && (
                            <p className="text-sm text-gray-500 mt-1 leading-relaxed">{angle.description}</p>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {showPromotions && (
                <PromotionConfigPanel
                  funnelIndex={funnelIndex}
                  promotion={funnel.promotion}
                  productPrice={productPrice}
                  onChange={(promotion) => onChangePromotion(funnelIndex, promotion)}
                />
              )}
            </div>
          );
        })}
        </div>
      </div>
    </div>
  );
}

function PromotionConfigPanel({
  funnelIndex,
  promotion,
  productPrice,
  onChange,
}: {
  funnelIndex: number;
  promotion?: PromotionConfig | null;
  productPrice: string;
  onChange: (promotion: PromotionConfig | null) => void;
}) {
  const isDisabled = !promotion?.enabled;
  const hasSelectedType = promotion?.enabled && isConfiguredPromotionType(promotion.type);
  const current = hasSelectedType ? normalizePromotion(promotion, funnelIndex) : createEmptyPromotion();
  const currentType: '' | Exclude<PromotionType, 'combo'> = hasSelectedType
    ? (current.type === 'buy_x_get_y' ? 'buy_x_get_y' : 'discount')
    : '';
  const currentTimeLimit = hasSelectedType ? normalizePromotionTimeLimit(current.timeLimit, current.limitTime) : 'unlimited';
  const basePrice = parsePromotionPrice(productPrice);
  const discountPercentage = Math.min(95, Math.max(1, Number(current.discountPercentage || 50)));
  const paidUnits = Math.max(1, Number(current.paidUnits || 2));
  const receivedUnits = Math.max(paidUnits + 1, Number(current.receivedUnits || paidUnits + 1));
  const pricePreview = currentType === 'discount'
    ? {
        current: basePrice ? basePrice * (1 - discountPercentage / 100) : 0,
        original: basePrice,
        caption: 'Precio con descuento',
      }
    : {
        current: basePrice ? basePrice * paidUnits : 0,
        original: basePrice ? basePrice * receivedUnits : 0,
        caption: `Pagas ${paidUnits} y recibes ${receivedUnits}`,
      };

  const updatePromotion = (patch: Partial<PromotionConfig>) => {
    const draft = { ...current, enabled: true, ...patch };
    const type: Exclude<PromotionType, 'combo'> = draft.type === 'buy_x_get_y' ? 'buy_x_get_y' : 'discount';
    const timeLimit = normalizePromotionTimeLimit(draft.timeLimit, draft.limitTime);
    const nextDiscountPercentage = Math.min(95, Math.max(1, Number(draft.discountPercentage || 50)));
    const nextPaidUnits = Math.max(1, Number(draft.paidUnits || 2));
    const nextReceivedUnits = Math.max(nextPaidUnits + 1, Number(draft.receivedUnits || nextPaidUnits + 1));

    onChange({
      enabled: true,
      type,
      label: buildPromotionLabel({
        type,
        discountPercentage: nextDiscountPercentage,
        paidUnits: nextPaidUnits,
        receivedUnits: nextReceivedUnits,
        timeLimit,
      }),
      limitTime: timeLimit !== 'unlimited',
      timeLimit,
      ...(type === 'discount'
        ? { discountPercentage: nextDiscountPercentage }
        : { paidUnits: nextPaidUnits, receivedUnits: nextReceivedUnits }),
    });
  };

  const updateType = (type: '' | Exclude<PromotionType, 'combo'>) => {
    if (!type) {
      onChange(createEmptyPromotion());
      return;
    }

    const next: Record<Exclude<PromotionType, 'combo'>, PromotionConfig> = {
      discount: {
        enabled: true,
        type: 'discount',
        label: buildPromotionLabel({ type: 'discount', discountPercentage, timeLimit: currentTimeLimit }),
        limitTime: currentTimeLimit !== 'unlimited',
        timeLimit: currentTimeLimit,
        discountPercentage: current.discountPercentage ?? 50,
      },
      buy_x_get_y: {
        enabled: true,
        type: 'buy_x_get_y',
        label: buildPromotionLabel({ type: 'buy_x_get_y', paidUnits, receivedUnits }),
        limitTime: currentTimeLimit !== 'unlimited',
        timeLimit: currentTimeLimit,
        paidUnits: current.paidUnits ?? 2,
        receivedUnits: Math.max(current.receivedUnits ?? 3, (current.paidUnits ?? 2) + 1),
      },
    };
    onChange(next[type]);
  };

  if (isDisabled) {
    return (
      <div className="flex min-h-[8rem] w-full self-center flex-col items-center justify-center rounded-2xl border border-dashed border-[#DDE2F1] bg-white px-4 py-4 text-center">
        <p className="text-sm font-bold text-gray-500">Sin promoción</p>
        <p className="mt-1 max-w-[14rem] text-xs leading-relaxed text-gray-400">Este embudo usará los selectores base de 1, 2 y 3 unidades.</p>
        <button
          type="button"
          onClick={() => onChange(createEmptyPromotion())}
          className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-fluxi/10 bg-fluxi/10 px-3 py-1.5 text-xs font-semibold text-fluxi transition-colors hover:bg-fluxi/15"
        >
          <Gift className="h-3 w-3" />
          Activar
        </button>
      </div>
    );
  }

  return (
    <div className="relative rounded-2xl border border-[#DDE2F1] bg-white px-3 py-3 shadow-[0_8px_18px_rgba(15,23,42,0.05)]">
      <button
        type="button"
        onClick={() => onChange(null)}
        aria-label="Quitar promoción"
        className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full border border-[#E2E8F0] bg-white text-gray-400 transition-colors hover:border-gray-300 hover:bg-gray-50 hover:text-gray-700"
      >
        <X className="h-3.5 w-3.5" />
      </button>

      <div className="mb-3 flex items-center gap-2 pr-8">
        <div className="flex min-w-0 items-center gap-2">
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-fluxi/10 text-fluxi">
            <Gift className="h-3 w-3" />
          </span>
          <div className="min-w-0">
            <p className="text-sm font-bold text-gray-900">Promoción</p>
          </div>
        </div>
      </div>

      <label className="block text-[11px] font-bold uppercase tracking-wide text-gray-400">Tipo de promoción</label>
      <div className="mt-1 flex items-center gap-2 rounded-lg border border-[#DDE2F1] bg-white px-3 py-2 focus-within:border-fluxi/40 focus-within:ring-2 focus-within:ring-fluxi/10">
        {currentType === 'discount' ? <Percent className="h-3.5 w-3.5 text-fluxi" /> : <Gift className="h-3.5 w-3.5 text-fluxi" />}
        <select
          value={currentType}
          onChange={(event) => updateType(event.target.value as '' | Exclude<PromotionType, 'combo'>)}
          className="min-w-0 flex-1 bg-transparent text-sm font-semibold text-gray-800 outline-none"
        >
          <option value="">Selecciona tipo</option>
          <option value="discount">{getPromotionTypeLabel('discount')}</option>
          <option value="buy_x_get_y">Compra X lleva Y</option>
        </select>
      </div>

      {currentType === 'discount' && (
        <div className="mt-2">
          <label className="block text-[11px] font-bold uppercase tracking-wide text-gray-400">Descuento</label>
          <div className="mt-1 flex items-center rounded-lg border border-[#DDE2F1] bg-white px-3 py-2 focus-within:border-fluxi/40 focus-within:ring-2 focus-within:ring-fluxi/10">
            <input
              type="number"
              min={1}
              max={95}
              value={discountPercentage}
              onChange={(event) => updatePromotion({ discountPercentage: Math.min(95, Math.max(1, Number(event.target.value) || 1)) })}
              className="min-w-0 flex-1 bg-transparent text-sm font-semibold text-gray-800 outline-none"
            />
            <span className="text-sm font-bold text-gray-400">%</span>
          </div>
        </div>
      )}

      {currentType === 'buy_x_get_y' && (
        <div className="mt-2 grid grid-cols-2 gap-2">
          <div>
            <label className="block text-[11px] font-bold uppercase tracking-wide text-gray-400">Paga</label>
            <input
              type="number"
              min={1}
              value={paidUnits}
              onChange={(event) => {
                const paidUnits = Math.max(1, Number(event.target.value) || 1);
                updatePromotion({ paidUnits, receivedUnits: Math.max(current.receivedUnits ?? paidUnits + 1, paidUnits + 1) });
              }}
              className="mt-1 w-full rounded-lg border border-[#DDE2F1] px-3 py-2 text-sm font-semibold text-gray-800 outline-none transition focus:border-fluxi/40 focus:ring-2 focus:ring-fluxi/10"
            />
          </div>
          <div>
            <label className="block text-[11px] font-bold uppercase tracking-wide text-gray-400">Lleva</label>
            <input
              type="number"
              min={paidUnits + 1}
              value={receivedUnits}
              onChange={(event) => {
                updatePromotion({ receivedUnits: Math.max(paidUnits + 1, Number(event.target.value) || paidUnits + 1) });
              }}
              className="mt-1 w-full rounded-lg border border-[#DDE2F1] px-3 py-2 text-sm font-semibold text-gray-800 outline-none transition focus:border-fluxi/40 focus:ring-2 focus:ring-fluxi/10"
            />
          </div>
        </div>
      )}

      {currentType && (
        <>
          <div className="mt-2 rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] px-3 py-2">
            {basePrice ? (
              <div className="flex items-center justify-between gap-3">
                <p className="min-w-0 text-xs font-medium text-gray-500">{pricePreview.caption}</p>
                <div className="flex shrink-0 items-baseline gap-2">
                  <span className="text-sm font-black text-gray-900">{formatPromotionPrice(pricePreview.current)}</span>
                  <span className="text-xs font-bold text-gray-400 line-through">{formatPromotionPrice(pricePreview.original)}</span>
                </div>
              </div>
            ) : (
              <p className="text-xs leading-relaxed text-gray-500">Agrega el precio del producto para ver el cálculo.</p>
            )}
          </div>

          <label className="mt-2 block text-[11px] font-bold uppercase tracking-wide text-gray-400">Tiempo límite</label>
          <div className="mt-1 flex items-center gap-2 rounded-lg border border-[#DDE2F1] bg-white px-3 py-2 focus-within:border-fluxi/40 focus-within:ring-2 focus-within:ring-fluxi/10">
            <Clock className="h-3.5 w-3.5 text-fluxi" />
            <select
              value={currentTimeLimit}
              onChange={(event) => {
                const timeLimit = event.target.value as PromotionTimeLimit;
                updatePromotion({ timeLimit, limitTime: timeLimit !== 'unlimited' });
              }}
              className="min-w-0 flex-1 bg-transparent text-sm font-semibold text-gray-800 outline-none"
            >
              <option value="unlimited">Sin límite</option>
              <option value="midnight">Hasta la media noche</option>
              <option value="60min">60 min</option>
              <option value="10min">10 min</option>
            </select>
          </div>
          {currentTimeLimit !== 'unlimited' && (
            <p className="mt-1.5 text-[10px] leading-relaxed text-gray-500">
              Contador visual: la compra sigue disponible aunque termine.
            </p>
          )}
        </>
      )}
    </div>
  );
}

function parsePromotionPrice(value: string | number | null | undefined) {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (!value) return 0;
  const normalized = String(value)
    .replace(/[^\d.,-]/g, '')
    .replace(/\.(?=\d{3}(\D|$))/g, '')
    .replace(',', '.');
  const parsed = Number(normalized);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

function formatPromotionPrice(value: number) {
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    maximumFractionDigits: 0,
  }).format(Math.max(0, value));
}

// ─── Sub-step 3: Ads with Funnel Sidebar ────────────────────
type AdStyleTab = 'all' | 'viral' | 'offer' | 'testimonials';
const AD_STYLE_TABS: AdStyleTab[] = ['all', 'viral', 'offer', 'testimonials'];
const MAX_AD_TEMPLATES = 20;

function AdsGlobalTab({
  funnels,
  adTemplates,
  isFetchingAdTemplates,
  onToggleAdTemplate,
  language,
}: {
  funnels: FunnelConfig[];
  adTemplates: AdStyle[];
  isFetchingAdTemplates: boolean;
  onToggleAdTemplate: (templateId: string) => void;
  language: Language;
}) {
  const [activeTab, setActiveTab] = useState<AdStyleTab>('all');

  // Use first funnel's selection as the global selection (all funnels are synced)
  const selectedTemplates = funnels[0]?.selectedAdTemplates || [];
  const isMaxReached = selectedTemplates.length >= MAX_AD_TEMPLATES;

  const filteredTemplates = useMemo(() => {
    if (activeTab === 'all') return adTemplates;
    return adTemplates.filter(t => t.category === activeTab);
  }, [adTemplates, activeTab]);

  const templateColumns = useMemo(() => {
    const cols: [AdStyle[], AdStyle[], AdStyle[], AdStyle[], AdStyle[]] = [[], [], [], [], []];
    filteredTemplates.forEach((t, i) => cols[i % 5].push(t));
    return cols;
  }, [filteredTemplates]);

  const getTabLabel = (tab: AdStyleTab): string => {
    const labels: Record<AdStyleTab, string> = {
      all: dictionary["all"]?.[language] ?? 'Todos',
      viral: dictionary["viral"]?.[language] ?? 'Viral',
      offer: dictionary["offer"]?.[language] ?? 'Oferta',
      testimonials: dictionary["testimonials"]?.[language] ?? 'Testimonios',
    };
    return labels[tab];
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-base font-bold text-gray-900">
          {dictionary["choose-ad-templates"]?.[language] ?? "Anuncios: Cada plantilla que elijas la convertiremos en un anuncio para tu producto"}
        </h2>
        {selectedTemplates.length > 0 && (
          <span className="text-sm font-bold text-fluxi whitespace-nowrap ml-3">
            {selectedTemplates.length}
          </span>
        )}
      </div>

      {/* Category tabs */}
      <div className="flex gap-2 mb-3 overflow-x-auto scrollbar-hide whitespace-nowrap pb-1">
        {AD_STYLE_TABS.map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => setActiveTab(tab)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
              activeTab === tab
                ? 'bg-[#1E1E2E] text-white'
                : 'bg-white border border-[#E2E8F0] text-gray-500 hover:border-gray-300 hover:text-gray-700'
            }`}
          >
            {getTabLabel(tab)}
          </button>
        ))}
      </div>

      {isMaxReached && (
        <div className="mb-3 flex items-center gap-2 rounded-lg bg-amber-50 px-3 py-2">
          <div className="flex items-center justify-center w-5 h-5 rounded-full bg-amber-500 text-white text-[10px] font-bold flex-shrink-0">!</div>
          <p className="text-xs text-amber-700 font-medium">
            {(dictionary["max-ad-templates-reached"]?.[language] ?? "Máximo de {n} plantillas alcanzado").replace("{n}", String(MAX_AD_TEMPLATES))}
          </p>
        </div>
      )}

      {/* Templates grid — 5 columns */}
      {isFetchingAdTemplates ? (
        <div className="max-h-[calc(100vh-320px)] overflow-y-auto pr-1">
          <div className="flex gap-3">
            {[0, 1, 2, 3, 4].map((col) => (
              <div key={col} className="flex-1 flex flex-col gap-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div
                    key={i}
                    className="rounded-xl bg-gray-100 animate-pulse"
                    style={{ height: (col + i) % 3 === 0 ? '180px' : (col + i) % 3 === 1 ? '140px' : '160px' }}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>
      ) : filteredTemplates.length > 0 ? (
        <div className="max-h-[calc(100vh-320px)] overflow-y-auto pr-1 p-1 -m-1">
          <div className="flex gap-3 items-start">
            {templateColumns.map((col, colIdx) => (
              <div key={colIdx} className="flex-1 flex flex-col gap-3">
                {col.map((template) => {
                  const isSelected = selectedTemplates.includes(template.id);
                  const disabled = isMaxReached && !isSelected;

                  return (
                    <div
                      key={template.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => !disabled && onToggleAdTemplate(template.id)}
                      onKeyDown={(e) => e.key === 'Enter' && !disabled && onToggleAdTemplate(template.id)}
                      className={`relative group cursor-pointer rounded-xl overflow-hidden transition-all duration-200 ${
                        isSelected
                          ? 'ring-2 ring-fluxi ring-offset-2'
                          : disabled
                            ? 'opacity-40 cursor-not-allowed'
                            : 'hover:scale-[1.02]'
                      }`}
                    >
                      <LazyImage
                        src={template.coverImage}
                        alt={template.name}
                        className="w-full block"
                        wrapperClassName="w-full"
                      />
                      {isSelected && (
                        <div className="absolute top-1.5 right-1.5 w-5 h-5 bg-fluxi rounded-full flex items-center justify-center z-10 shadow">
                          <Check className="h-3 w-3 text-white" strokeWidth={3} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-center h-32 text-gray-400">
          <p className="text-sm">No se encontraron plantillas</p>
        </div>
      )}
    </div>
  );
}

// ─── Angles Loading Screen (multi-step animation) ───────────
function AnglesLoadingScreen({ language }: { language: Language }) {
  const steps = [
    {
      icon: Sparkles,
      label: dictionary["analyzing-product"]?.[language] ?? "Haciendo investigación de mercado...",
    },
    {
      icon: Target,
      label: dictionary["loading-personalized-sales-angles"]?.[language] ?? "Buscando reseñas de productos similares...",
    },
    {
      icon: BarChart3,
      label: dictionary["optimizing-sections"]?.[language] ?? "Encontrando dolores de usuarios reales...",
    },
  ];

  return (
    <AnalysisLoadingScreen
      title={dictionary["fluxi-analyzing-title"]?.[language] ?? "Fluxi está analizando tu producto"}
      subtitle={dictionary["fluxi-analyzing-subtitle"]?.[language] ?? "Estamos encontrando los mejores ángulos de venta para tu producto"}
      steps={steps}
    />
  );
}

function AnglesRetryScreen({
  language,
  message,
  onRetry,
}: {
  language: Language;
  message: string | null;
  onRetry?: () => void;
}) {
  return (
    <div className="flex min-h-[520px] flex-col items-center justify-center px-4 py-8 text-center">
      <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-red-50 text-red-500">
        <AlertCircle className="h-8 w-8" />
      </div>
      <h2 className="mb-2 text-xl font-bold text-gray-900">
        {dictionary["sales-angles-error-title"]?.[language] ?? "No pudimos generar los ángulos"}
      </h2>
      <p className="mb-6 max-w-md text-sm leading-relaxed text-[#64748B]">
        {message ?? dictionary["sales-angles-error-message"]?.[language] ?? "No pudimos generar ángulos de venta de calidad. Intenta de nuevo."}
      </p>
      <button
        type="button"
        onClick={onRetry}
        className="inline-flex items-center gap-2 rounded-full bg-fluxi px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-fluxi-dark"
      >
        <RefreshCw className="h-4 w-4" />
        {dictionary.retry?.[language] ?? "Reintentar"}
      </button>
    </div>
  );
}
